import React from 'react'
import { Field, FieldProps } from 'formik'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import brreg from 'brreg'
import {
  Box,
  Flex,
  FormField,
  Input,
  Logo,
  SearchSelect,
  Text,
  H2,
} from '../../../components'
import { SettingsQuery } from '../../../lib/core'
import { labelMessages } from '../messages'
import { Values } from '../types'
import { useDebounce } from '../../../lib/use-debounce'

type Props = {
  enableBRREGLookup: boolean
}

const messages = defineMessages({
  title: {
    id: 'modules.Registration.CompanyStep.title',
    defaultMessage: 'Registration',
  },
  description: {
    id: 'modules.Registration.CompanyStep.description',
    defaultMessage:
      'Register your company to create and share your voucher codes. Each code will give access to {systemName}.',
  },
  NAME_REQUIRED: {
    id: 'modules.Registration.CompanyStep.NAME_REQUIRED',
    defaultMessage: 'Name is required',
  },
  ORG_NR_REQUIRED: {
    id: 'modules.Registration.CompanyStep.ORG_NR_REQUIRED',
    defaultMessage: 'Organization number is required',
  },
  SIRET_NR_REQUIRED: {
    id: 'modules.Registration.CompanyStep.SIRET_NR_REQUIRED',
    defaultMessage: 'SIRET number is required',
  },
  SIRET_NR_INVALID: {
    id: 'modules.Registration.CompanyStep.SIRET_NR_INVALID',
    defaultMessage: 'SIRET number is invalid',
  },
})

function brregReducer(state, action) {
  switch (action.type) {
    case 'ON_INPUT_CHANGE':
      return { ...state, query: action.payload }
    case 'LOADING':
      return { ...state, loading: true }
    case 'SET_ITEMS':
      return { ...state, loading: false, items: action.payload }
    default:
      throw new Error()
  }
}

function validateCompanyName(value) {
  let error
  if (!value) {
    error = 'NAME_REQUIRED'
  }
  return error
}

function validateSiretNr(value) {
  let error
  if (!value) {
    error = 'SIRET_NR_REQUIRED'
  } else if (!/^\d{14}$/.test(value)) {
    error = 'SIRET_NR_INVALID'
  }
  return error
}

function validateCompanyOrgNr(value) {
  let error
  if (!value) {
    error = 'ORG_NR_REQUIRED'
  }
  return error
}

const CompanyStep = ({ enableBRREGLookup }: Props) => {
  const intl = useIntl()
  const [state, dispatch] = React.useReducer(brregReducer, {
    items: [],
    query: '',
    loading: false,
  })
  const debouncedBrregQuery = useDebounce<string>(state.query, 500)

  React.useEffect(() => {
    if (debouncedBrregQuery) {
      dispatch({ type: 'LOADING' })
      brreg({ query: debouncedBrregQuery }).then((value) => {
        dispatch({
          type: 'SET_ITEMS',
          payload: value.enhetsregisteret.data.entries
            .slice(0, 10)
            .map((enhet) => ({ value: enhet.navn, ...enhet })),
        })
      })
    }
  }, [debouncedBrregQuery])

  return (
    <SettingsQuery>
      {({ data, loading }) => {
        if (loading || !data || !data.system) {
          return null
        }

        return (
          <Flex
            flex={1}
            flexWrap="wrap"
            mb="sm"
            textAlign="center"
            alignItems="center"
            column
          >
            <Flex mb="sm" column alignItems="center">
              <Logo companyName={null} title={null} color="primary" />

              <H2 mb={0} typoStyle="md" mt="sm">
                <FormattedMessage {...messages.title} />
              </H2>

              <Box maxWidth="300px" mx="auto">
                <Text typoStyle="xxs" mt="xs" mute>
                  <FormattedMessage
                    {...messages.description}
                    values={{ systemName: data.system.name }}
                  />
                </Text>
              </Box>
            </Flex>

            <Box width="22rem">
              <Field name="companyName" validate={validateCompanyName}>
                {({
                  field,
                  form,
                }: FieldProps<Values['companyName'], Values>) => {
                  const error =
                    form.touched[field.name] && form.errors[field.name]
                  const errorMessage = error && messages[error] && (
                    <FormattedMessage {...messages[error]} />
                  )
                  const formFieldProps = {
                    label: intl.formatMessage(labelMessages.companyName),
                    errorMessage,
                  }

                  if (enableBRREGLookup) {
                    const { setFieldValue } = form
                    return (
                      <SearchSelect
                        id="company-name"
                        {...formFieldProps}
                        onChange={(value) => {
                          if (!value) {
                            return
                          }
                          setFieldValue('companyName', value.navn)
                          setFieldValue('companyOrgNr', value.orgnr)
                          setFieldValue('address', value.forretningsadr)
                          setFieldValue('zipCode', value.forradrpostnr)
                          setFieldValue('city', value.forradrpoststed)
                        }}
                        items={state.items}
                        onInputValueChange={(inputValue) => {
                          setFieldValue('companyName', inputValue)
                          dispatch({
                            type: 'ON_INPUT_CHANGE',
                            payload: inputValue,
                          })
                        }}
                        inputValue={field.value}
                        autoFocus
                      />
                    )
                  }
                  return (
                    <FormField wrapperProps={{ mb: 'sm' }} {...formFieldProps}>
                      <Input id={field.name} {...field} alternative autoFocus />
                    </FormField>
                  )
                }}
              </Field>

              <Field
                name="companyOrgNr"
                validate={
                  data?.system?.id === 'inurba-rouen'
                    ? validateSiretNr
                    : validateCompanyOrgNr
                }
              >
                {({
                  field,
                  form,
                }: FieldProps<Values['companyOrgNr'], Values>) => {
                  const error =
                    form.touched[field.name] && form.errors[field.name]
                  const errorMessage = error && messages[error] && (
                    <FormattedMessage {...messages[error]} />
                  )

                  return (
                    <FormField
                      wrapperProps={{ mb: 'sm' }}
                      label={
                        data?.system?.id === 'inurba-rouen'
                          ? intl.formatMessage(labelMessages.siretNr)
                          : intl.formatMessage(labelMessages.companyOrgNr)
                      }
                      errorMessage={errorMessage}
                    >
                      <Input id={field.name} {...field} alternative />
                    </FormField>
                  )
                }}
              </Field>
            </Box>
          </Flex>
        )
      }}
    </SettingsQuery>
  )
}

export default CompanyStep
